/**
 * @file FullscreenLayout.jsx
 * @description Fullscreen mod layout yönlendiricisi
 *
 * Responsive tasarım ile cihaz tipine göre uygun layout'u seçer.
 * Masaüstü için DesktopLayout (yan yana liste + harita),
 * mobil için MobileCarouselLayout (harita + karusel) kullanır.
 *
 * @module widget/components/FullscreenLayout
 */

import { useMemo, memo } from "react";
import { useMaxHeight } from "../../use-max-height";
import { MobileCarouselLayout, DesktopLayout } from "./layouts";

/**
 * Fullscreen Layout Router Bileşeni
 *
 * ChatGPT'nin requestDisplayMode('fullscreen') ile tetiklediği
 * tam ekran modunu yönetir. CSS breakpoint'leri ile responsive
 * layout seçimi yapar.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object[]} props.listings - İlan listesi
 * @param {Object} props.filters - Aktif filtreler
 * @param {number} props.total - Toplam sonuç sayısı
 * @param {boolean} props.isDark - Karanlık mod
 * @param {Function} props.onRemoveFilter - Filtre kaldırma
 * @param {Function} props.onApplyFilter - Filtre uygulama
 * @param {Function} props.onAddFilter - Filtre ekleme
 * @param {Function} props.onDetailClick - Detay açma
 * @param {Function} props.onExitFullscreen - Fullscreen'den çıkış
 * @param {Function} props.openExternal - Harici link açma
 * @param {number|null} props.selectedListingId - Seçili ilan
 * @param {Object|null} props.listingDetail - İlan detay verisi
 * @param {boolean} props.detailLoading - Detay yükleniyor
 * @param {number|null} props.regionAveragePrice - Bölge ortalaması
 * @param {number} props.selectedImageIndex - Seçili görsel
 * @param {Function} props.setSelectedImageIndex - Görsel seçimi
 * @param {Function} props.onCloseDetail - Detay kapatma
 * @param {Function} props.onMapSearchInArea - Harita arama
 * @param {boolean} props.isMapSearching - Arama devam ediyor
 * @param {boolean} [props.mapSearchEmpty=false] - Boş sonuç
 * @param {Set<string>} [props.savedListingIdSet] - Kaydedilen ilan ID'leri Set'i
 * @param {Function} [props.onToggleSave] - Kaydetme toggle callback'i
 * @param {boolean} [props.isSaveAtLimit] - Kaydetme limiti doldu mu
 * @param {Set<string>} [props.compareListingIdSet] - Karşılaştırmadaki ilan ID'leri Set'i
 * @param {Function} [props.onToggleCompare] - Karşılaştırma toggle callback'i
 * @param {boolean} [props.isCompareAtLimit] - Karşılaştırma limiti doldu mu
 *
 * @returns {JSX.Element} Layout router bileşeni
 *
 * @example
 * <FullscreenLayout
 *   listings={listings}
 *   filters={filters}
 *   isDark={theme === 'dark'}
 *   onExitFullscreen={() => exitFullscreen()}
 *   {...otherProps}
 * />
 *
 * @remarks
 * - sm breakpoint (640px) altında MobileCarouselLayout
 * - sm breakpoint üstünde DesktopLayout kullanılır
 * - useMaxHeight hook'u ChatGPT iframe yüksekliğini alır
 */
function FullscreenLayoutInner({
  listings,
  filters,
  total,
  isDark,
  onRemoveFilter,
  onApplyFilter,
  onAddFilter,
  onDetailClick,
  onExitFullscreen,
  openExternal,
  selectedListingId,
  listingDetail,
  detailLoading,
  regionAveragePrice,
  selectedImageIndex,
  setSelectedImageIndex,
  onCloseDetail,
  onMapSearchInArea,
  isMapSearching,
  mapSearchEmpty = false,
  // Save & Compare props
  savedListingIdSet,
  onToggleSave,
  isSaveAtLimit = false,
  compareListingIdSet,
  onToggleCompare,
  isCompareAtLimit = false,
}) {
  const hostMaxHeight = useMaxHeight();
  const containerHeight = hostMaxHeight || '100vh';

  // Memoize sharedProps to prevent unnecessary re-renders of child layouts
  const sharedProps = useMemo(() => ({
    listings,
    filters,
    total,
    isDark,
    containerHeight,
    onRemoveFilter,
    onApplyFilter,
    onAddFilter,
    onDetailClick,
    onExitFullscreen,
    openExternal,
    selectedListingId,
    listingDetail,
    detailLoading,
    regionAveragePrice,
    selectedImageIndex,
    setSelectedImageIndex,
    onCloseDetail,
    onMapSearchInArea,
    isMapSearching,
    mapSearchEmpty,
    // Save & Compare props
    savedListingIdSet,
    onToggleSave,
    isSaveAtLimit,
    compareListingIdSet,
    onToggleCompare,
    isCompareAtLimit,
  }), [
    listings,
    filters,
    total,
    isDark,
    containerHeight,
    onRemoveFilter,
    onApplyFilter,
    onAddFilter,
    onDetailClick,
    onExitFullscreen,
    openExternal,
    selectedListingId,
    listingDetail,
    detailLoading,
    regionAveragePrice,
    selectedImageIndex,
    setSelectedImageIndex,
    onCloseDetail,
    onMapSearchInArea,
    isMapSearching,
    mapSearchEmpty,
    savedListingIdSet,
    onToggleSave,
    isSaveAtLimit,
    compareListingIdSet,
    onToggleCompare,
    isCompareAtLimit,
  ]);

  return (
    <div
      className={`w-full ${isDark ? "bg-zinc-900" : "bg-gray-50"}`}
      style={{
        height: containerHeight,
        maxHeight: containerHeight,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Mobile Layout - TripAdvisor-style carousel + map */}
      <div className="sm:hidden flex flex-col flex-1 min-h-0">
        <MobileCarouselLayout {...sharedProps} />
      </div>

      {/* Desktop Layout */}
      <div className="hidden sm:flex flex-1 min-h-0">
        <DesktopLayout {...sharedProps} />
      </div>
    </div>
  );
}

/**
 * Memoized FullscreenLayout - heavy component, memo ile sarmalandı
 * re-render'ları minimize etmek için.
 */
export const FullscreenLayout = memo(FullscreenLayoutInner);
export default FullscreenLayout;
